import React, { useState, useEffect, useRef } from 'react';
import { LYT_INFO } from '../config/constants';
import { Printer, ChevronDown, ChevronRight, BookOpen, Users, HardHat, Building2, Shield, Zap, ArrowLeft } from 'lucide-react';

// ============================================================
// LYT Communications — Platform User Guide
// Version 1.0.0 — March 2026
// ============================================================

const ALL_SECTIONS = [
  { id: 'getting-started', title: 'Getting Started', icon: '🚀', roles: ['admin', 'employee', 'contractor', 'sub'] },
  { id: 'employee-guide', title: 'Employee Guide', icon: '👷', roles: ['admin', 'employee'] },
  { id: 'contractor-guide', title: 'Contractor Guide', icon: '🏗️', roles: ['admin', 'contractor'] },
  { id: 'sub-contractor-guide', title: 'Sub-Contractor Guide', icon: '🔧', roles: ['admin', 'sub'] },
  { id: 'admin-guide', title: 'Administrator Guide', icon: '🛡️', roles: ['admin'] },
  { id: 'quick-reference', title: 'Quick Reference', icon: '📋', roles: ['admin', 'employee', 'contractor', 'sub'] },
];

const ROLE_LABELS = {
  admin: 'Administrator',
  employee: 'Employee',
  contractor: 'Contractor',
  sub: 'Sub-Contractor',
};

const ROLE_COLORS = {
  admin: '#9C27B0',
  employee: '#4CAF50',
  contractor: '#2196F3',
  sub: '#FF9800',
};

const ROLE_ICONS_MAP = {
  employee: HardHat,
  contractor: Building2,
  sub: Users,
  admin: Shield,
};

// ── Reusable Components ──────────────────────────────────────

function SectionHeader({ id, title, subtitle, darkMode }) {
  return (
    <div id={id} style={{ scrollMarginTop: 80, pageBreakBefore: 'always', marginBottom: 32, paddingTop: 40 }}>
      <h2 style={{ fontSize: 28, fontWeight: 700, color: darkMode ? '#fff' : '#0f172a', marginBottom: 6, letterSpacing: -0.5 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 15, color: darkMode ? '#94a3b8' : '#64748b', lineHeight: 1.6, maxWidth: 700 }}>{subtitle}</p>}
      <div style={{ width: 60, height: 3, background: darkMode ? 'linear-gradient(90deg, #c850c0, #ff6b35)' : 'linear-gradient(90deg, #0077B6, #00b4d8)', borderRadius: 2, marginTop: 12 }} />
    </div>
  );
}

function SubSection({ title, darkMode, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{ fontSize: 20, fontWeight: 600, color: darkMode ? '#e2e8f0' : '#1e293b', marginBottom: 12 }}>{title}</h3>
      <div style={{ color: darkMode ? '#cbd5e1' : '#475569', fontSize: 15, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function StepList({ steps, darkMode }) {
  const accent = darkMode ? '#c850c0' : '#0077B6';
  return (
    <ol style={{ paddingLeft: 0, listStyle: 'none', margin: '16px 0' }}>
      {steps.map((step, i) => (
        <li key={i} style={{ display: 'flex', gap: 14, marginBottom: 16, alignItems: 'flex-start' }}>
          <span style={{
            minWidth: 28, height: 28, borderRadius: '50%', background: accent,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, marginTop: 2,
          }}>{i + 1}</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: darkMode ? '#e2e8f0' : '#1e293b' }}>{step.title}</strong>
            {step.desc && <p style={{ margin: '4px 0 0', color: darkMode ? '#94a3b8' : '#64748b', fontSize: 14 }}>{step.desc}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Tip({ type = 'info', children, darkMode }) {
  const config = {
    info: { bg: '#e0f2fe', border: '#0284c7', icon: 'ℹ️', label: 'Tip', darkBg: '#0c4a6e30', darkBorder: '#0284c7' },
    warning: { bg: '#fef3c7', border: '#d97706', icon: '⚠️', label: 'Important', darkBg: '#78350f30', darkBorder: '#d97706' },
    success: { bg: '#dcfce7', border: '#16a34a', icon: '✅', label: 'Note', darkBg: '#14532d30', darkBorder: '#16a34a' },
    danger: { bg: '#fee2e2', border: '#dc2626', icon: '🚫', label: 'Warning', darkBg: '#7f1d1d30', darkBorder: '#dc2626' },
  }[type];
  return (
    <div style={{
      background: darkMode ? config.darkBg : config.bg,
      border: `1px solid ${darkMode ? config.darkBorder : config.border}`,
      borderLeft: `4px solid ${config.border}`,
      borderRadius: 8, padding: '14px 18px', margin: '16px 0', fontSize: 14, lineHeight: 1.6,
      color: darkMode ? '#e2e8f0' : '#1e293b',
    }}>
      <strong>{config.icon} {config.label}:</strong> {children}
    </div>
  );
}

function GuideImage({ src, alt, darkMode }) {
  return (
    <div style={{ margin: '20px 0', borderRadius: 12, overflow: 'hidden', border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
      <img src={src} alt={alt} style={{ width: '100%', display: 'block' }} loading="lazy" />
      <div style={{ padding: '8px 14px', fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b', background: darkMode ? '#1e293b' : '#f8fafc', borderTop: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>{alt}</div>
    </div>
  );
}

function WireframePlaceholder({ description, darkMode }) {
  return (
    <div style={{
      background: darkMode ? '#1e293b' : '#f1f5f9',
      border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
      borderRadius: 12, padding: '32px 24px', margin: '20px 0',
      textAlign: 'center', color: darkMode ? '#64748b' : '#94a3b8',
    }}>
      <div style={{ fontSize: 13, fontStyle: 'italic' }}>{description}</div>
    </div>
  );
}

function DataTable({ headers, rows, darkMode }) {
  const border = darkMode ? '#334155' : '#e2e8f0';
  const headerBg = darkMode ? '#1e293b' : '#f1f5f9';
  return (
    <div style={{ overflowX: 'auto', margin: '16px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>{headers.map((h, i) => (
            <th key={i} style={{ textAlign: 'left', padding: '10px 14px', background: headerBg, borderBottom: `2px solid ${border}`, fontWeight: 600, color: darkMode ? '#e2e8f0' : '#1e293b', whiteSpace: 'nowrap' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>{row.map((cell, ci) => (
              <td key={ci} style={{ padding: '8px 14px', borderBottom: `1px solid ${border}`, color: darkMode ? '#cbd5e1' : '#475569' }}>{cell}</td>
            ))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ label, color }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      background: color + '20', color, fontSize: 12, fontWeight: 600, marginRight: 6,
    }}>{label}</span>
  );
}

function Card({ darkMode, children, style }) {
  return (
    <div style={{
      background: darkMode ? '#112240' : '#f8fafc',
      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
      borderRadius: 12, padding: '24px 28px', marginBottom: 24, ...style,
    }}>{children}</div>
  );
}

// ── Main Component ───────────────────────────────────────────

export default function UserGuidePage({ darkMode, setCurrentPage, loggedInUser }) {
  const detectedRole = loggedInUser?.role || null;
  const [viewRole, setViewRole] = useState(detectedRole || 'admin');
  const [activeSection, setActiveSection] = useState('getting-started');
  const contentRef = useRef(null);

  // Filter sections by selected role
  const sections = ALL_SECTIONS.filter(s => s.roles.includes(viewRole)).map((s, i) => ({
    ...s,
    title: `${i + 1}. ${s.title}`,
  }));

  // Track scroll position for TOC highlight
  useEffect(() => {
    const handleScroll = () => {
      const els = sections.map(s => document.getElementById(s.id)).filter(Boolean);
      for (let i = els.length - 1; i >= 0; i--) {
        if (els[i].getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const text = darkMode ? '#cbd5e1' : '#475569';
  const heading = darkMode ? '#ffffff' : '#0f172a';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const border = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const tocBg = darkMode ? '#0a1628' : '#f8fafc';

  // Section visibility + numbering helpers
  const visibleIds = new Set(sections.map(s => s.id));
  const sn = (id) => {
    const idx = sections.findIndex(s => s.id === id);
    return idx >= 0 ? `${idx + 1}.` : '';
  };

  return (
    <div style={{ backgroundColor: bg, minHeight: '100vh' }}>
      {/* Print Styles */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .guide-content { margin: 0 !important; max-width: 100% !important; padding: 0 20px !important; }
          .screenshot-placeholder { border-color: #ccc !important; background: #f9f9f9 !important; }
          * { color: #1e293b !important; background: transparent !important; border-color: #e2e8f0 !important; }
          h1, h2, h3, h4 { color: #0f172a !important; }
          table { page-break-inside: avoid; }
          h2 { page-break-before: always; }
          h2:first-of-type { page-break-before: avoid; }
          a { text-decoration: none !important; color: #0077B6 !important; }
        }
      `}</style>

      {/* Top Bar */}
      <div className="no-print" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: darkMode ? '#112240' : '#fff',
        borderBottom: `1px solid ${border}`,
        padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {setCurrentPage && (
            <button onClick={() => setCurrentPage('home')} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
              <ArrowLeft size={16} /> Back
            </button>
          )}
          <BookOpen size={20} color={accent} />
          <span style={{ fontWeight: 700, fontSize: 16, color: heading }}>LYT Platform User Guide</span>
          <span style={{ fontSize: 12, color: text, background: darkMode ? '#1e293b' : '#e2e8f0', padding: '2px 8px', borderRadius: 8 }}>v1.0</span>
        </div>
        <button onClick={() => window.print()} style={{
          background: accent, color: '#fff', border: 'none', borderRadius: 8,
          padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
        }}>
          <Printer size={14} /> Export PDF
        </button>
      </div>

      <div style={{ display: 'flex', maxWidth: 1400, margin: '0 auto' }}>
        {/* TOC Sidebar (desktop) */}
        <nav className="no-print" style={{
          width: 260, minWidth: 260, position: 'sticky', top: 60, alignSelf: 'flex-start',
          height: 'calc(100vh - 60px)', overflowY: 'auto', padding: '24px 16px',
          background: tocBg, borderRight: `1px solid ${border}`,
        }}>
          {/* Role selector (only when not logged in) */}
          {!detectedRole && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: text, marginBottom: 10 }}>View as</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {Object.entries(ROLE_LABELS).map(([role, label]) => (
                  <button key={role} onClick={() => { setViewRole(role); setActiveSection('getting-started'); }} style={{
                    padding: '5px 10px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    background: viewRole === role ? ROLE_COLORS[role] : (darkMode ? '#1e293b' : '#e2e8f0'),
                    color: viewRole === role ? '#fff' : (darkMode ? '#94a3b8' : '#64748b'),
                    transition: 'all 0.15s',
                  }}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ borderBottom: `1px solid ${border}`, margin: '12px 0' }} />
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: text, marginBottom: 16 }}>Contents</div>
          {sections.map(s => (
            <button key={s.id} onClick={() => scrollTo(s.id)} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 2,
              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
              background: activeSection === s.id ? (darkMode ? '#1e293b' : '#e0f2fe') : 'transparent',
              color: activeSection === s.id ? accent : (darkMode ? '#94a3b8' : '#64748b'),
              fontWeight: activeSection === s.id ? 600 : 400,
              transition: 'all 0.15s',
            }}>
              <span style={{ marginRight: 8 }}>{s.icon}</span>{s.title}
            </button>
          ))}
          <div style={{ borderTop: `1px solid ${border}`, margin: '20px 0', paddingTop: 16 }}>
            <div style={{ fontSize: 11, color: text }}>LYT Communications</div>
            <div style={{ fontSize: 11, color: text }}>Last updated: March 2026</div>
          </div>
        </nav>

        {/* Main Content */}
        <div className="guide-content" ref={contentRef} style={{ flex: 1, padding: '8px 48px 80px', maxWidth: 900, color: text }}>

          {/* Cover */}
          <div style={{ textAlign: 'center', padding: '48px 0 24px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: accent, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>LYT Communications</div>
            <h1 style={{ fontSize: 38, fontWeight: 800, color: heading, marginBottom: 8, letterSpacing: -1 }}>
              {detectedRole ? `${ROLE_LABELS[detectedRole]} Guide` : 'Platform User Guide'}
            </h1>
            <p style={{ fontSize: 16, color: text, maxWidth: 500, margin: '0 auto 16px' }}>
              {detectedRole
                ? `Everything you need to know as a${detectedRole === 'admin' || detectedRole === 'employee' ? 'n' : ''} ${ROLE_LABELS[detectedRole].toLowerCase()}`
                : 'Select your role to see relevant instructions'}
            </p>
            {detectedRole && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px',
                background: ROLE_COLORS[viewRole] + '20', borderRadius: 20, fontSize: 13, color: ROLE_COLORS[viewRole], fontWeight: 600,
              }}>
                {React.createElement(ROLE_ICONS_MAP[viewRole], { size: 16 })}
                {ROLE_LABELS[viewRole]}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, fontSize: 13, color: text, flexWrap: 'wrap', marginTop: 16 }}>
              <span>Version 1.0</span>
              <span>•</span>
              <span>March 2026</span>
              <span>•</span>
              <span>lytcomm.com</span>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════
              SECTION: GETTING STARTED
             ════════════════════════════════════════════════════════ */}
          {visibleIds.has('getting-started') && <>
          <SectionHeader id="getting-started" title={sn('getting-started') + ' Getting Started'} subtitle="Everything you need to know to access and navigate the LYT Communications platform." darkMode={darkMode} />

          <SubSection title="Creating Your Account" darkMode={darkMode}>
            <p>All new team members receive an invite from LYT management. Your account is created by an administrator — you'll receive a welcome email with a link to set your password.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Check your email', desc: 'Look for a welcome email from LYT Communications with the subject "Welcome to LYT Communications".' },
              { title: 'Click the setup link', desc: 'The email contains a secure link to set your password. This link is valid for 48 hours.' },
              { title: 'Create your password', desc: 'Your password must be at least 8 characters with one uppercase letter, one lowercase letter, and one number.' },
              { title: 'Log in', desc: 'After setting your password, you\'ll be redirected to the login page. Enter your email and new password.' },
            ]} />
            <GuideImage src="/guide/12-set-password.png" alt="Set Password page — create your password after receiving the welcome email" darkMode={darkMode} />
          </SubSection>

          <SubSection title="Logging In" darkMode={darkMode}>
            <p>Access the platform at <strong>lytcomm.com</strong> and click <strong>Portal</strong> in the top navigation.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Navigate to lytcomm.com', desc: 'Open your browser and go to the LYT Communications website.' },
              { title: 'Click "Portal"', desc: 'The Portal button is in the top-right corner of the navigation bar.' },
              { title: 'Enter your credentials', desc: 'Type your email address and password, then click Sign In.' },
              { title: 'You\'re in', desc: 'You\'ll be automatically directed to your role-specific dashboard.' },
            ]} />
            <GuideImage src="/guide/01-portal-login.png" alt="Portal Login — enter your email and password to access your dashboard" darkMode={darkMode} />
            <Tip type="info" darkMode={darkMode}>Your dashboard is determined by your role: Employee Dashboard, Contractor Dashboard, or Admin Dashboard.</Tip>
          </SubSection>

          <SubSection title="Resetting Your Password" darkMode={darkMode}>
            <p>If you forget your password, click <strong>"Forgot your password?"</strong> on the login page. Enter your email address, and you'll receive a reset link. For security, the system always confirms the email was sent — even if the address isn't registered.</p>
          </SubSection>

          <SubSection title="Your Profile" darkMode={darkMode}>
            <p>All users can update their profile information, change their password, and configure notification preferences.</p>
            <Card darkMode={darkMode}>
              <p><strong>Profile Tab:</strong> Update your name, email, phone number</p>
              <p><strong>Password Tab:</strong> Change your current password</p>
              <p><strong>Notifications Tab:</strong> Toggle email alerts, SMS alerts, certification expiry warnings, new assignment alerts, and weekly digest</p>
              <p><strong>Emergency Contact Tab:</strong> Update your emergency contact information</p>
            </Card>
          </SubSection>

          <SubSection title="Dark / Light Mode" darkMode={darkMode}>
            <p>Click the <strong>sun/moon icon</strong> in the top-right corner of any page to toggle between dark and light themes. Your preference is saved automatically and persists between sessions.</p>
          </SubSection>

          {/* Field Assist (AI Chat) — disabled for now */}
          </>}

          {/* ════════════════════════════════════════════════════════
              SECTION: EMPLOYEE GUIDE
             ════════════════════════════════════════════════════════ */}
          {visibleIds.has('employee-guide') && <>
          <SectionHeader id="employee-guide" title={sn('employee-guide') + ' Employee Guide'} subtitle="For W-2 employees — time tracking, daily production, safety compliance, and field documentation." darkMode={darkMode} />

          <SubSection title="Dashboard Overview" darkMode={darkMode}>
            <p>Your dashboard is your home base. It shows a time-of-day greeting, quick-action cards, and your active time clock.</p>
            <Card darkMode={darkMode}>
              <p><strong>Quick Action Cards:</strong></p>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li><strong>Log Production</strong> — Record footage, splices, and drops for the day</li>
                <li><strong>Equipment Check</strong> — Complete your pre-shift vehicle safety inspection</li>
                <li><strong>Safety Talk</strong> — Log toolbox talks and attendees</li>
                <li><strong>Report Incident</strong> — Document incidents, near misses, or utility strikes</li>
              </ul>
            </Card>
            <GuideImage src="/guide/08-employee-dashboard.png" alt="Employee Dashboard — greeting, time clock widget, and quick action cards" darkMode={darkMode} />
          </SubSection>

          <SubSection title="Time Clock" darkMode={darkMode}>
            <p>The time clock widget is on your dashboard. It tracks your shift with a live running timer.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Clock In', desc: 'Tap the green "Clock In" button at the start of your shift. The timer starts immediately.' },
              { title: 'Work your shift', desc: 'The timer runs in HH:MM:SS format. Your GPS location is recorded at clock-in.' },
              { title: 'Clock Out', desc: 'Tap the red "Clock Out" button. Your total hours are logged to the timesheet.' },
            ]} />
            <Tip type="warning" darkMode={darkMode}>Do not forget to clock out. If you leave without clocking out, notify your supervisor to correct the record.</Tip>
          </SubSection>

          <SubSection title="Daily Production" darkMode={darkMode}>
            <p>Log your daily production numbers at the end of each shift.</p>
            <Card darkMode={darkMode}>
              <p><strong>Fields:</strong></p>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li><strong>Project</strong> — Select from your assigned projects</li>
                <li><strong>Fiber Footage</strong> — Total linear feet of fiber installed</li>
                <li><strong>Splices</strong> — Number of fusion splices completed</li>
                <li><strong>Drops</strong> — Customer drops installed</li>
                <li><strong>Poles</strong> — Poles set or worked</li>
                <li><strong>HDD Footage</strong> — Horizontal directional drilling footage</li>
                <li><strong>Notes</strong> — Any additional information for the day</li>
              </ul>
            </Card>
          </SubSection>

          <SubSection title="Equipment Checks" darkMode={darkMode}>
            <p>Complete a vehicle and equipment safety inspection before each shift. This is required by DOT and company policy.</p>
            <Card darkMode={darkMode}>
              <p><strong>Checklist Items:</strong></p>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li>Vehicle ID and current mileage</li>
                <li>Tire condition and pressure</li>
                <li>Lights and signals working</li>
                <li>Fluid levels (oil, coolant, hydraulic)</li>
                <li>Safety equipment present (cones, fire extinguisher, first aid)</li>
                <li>Any issues or damage to report</li>
              </ul>
            </Card>
          </SubSection>

          <SubSection title="Safety / Toolbox Talks" darkMode={darkMode}>
            <p>Log your daily safety briefing. Select the project, enter the topic discussed, list all attendees, and sign.</p>
          </SubSection>

          <SubSection title="Viewing the Project Map" darkMode={darkMode}>
            <p>Navigate to <strong>Project Map</strong> from your sidebar to view the interactive map. As an employee, you can view segment details, status colors, and crew locations — but status changes are made by contractors or admins.</p>
            <GuideImage src="/guide/03-project-map.png" alt="Project Map — color-coded segments showing boring, pulling, and splicing status" darkMode={darkMode} />
          </SubSection>

          <SubSection title="Pothole Documentation" darkMode={darkMode}>
            <p>When you encounter a pothole during boring operations, document it for approval.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Go to Pothole Docs', desc: 'From your sidebar, select "Pothole Docs".' },
              { title: 'Create New Report', desc: 'Enter the project, address, size (small/medium/large), and surface type.' },
              { title: 'Add photos', desc: 'Take photos of the pothole showing depth and utility exposure.' },
              { title: 'Submit', desc: 'Your report goes to admin for review and approval.' },
            ]} />
          </SubSection>

          <SubSection title="Incident Reporting" darkMode={darkMode}>
            <p>Report incidents immediately using the Incident Report form.</p>
            <Card darkMode={darkMode}>
              <p><strong>Incident Types:</strong></p>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li><strong>Near Miss</strong> — An event that could have caused harm but didn't</li>
                <li><strong>Property Damage</strong> — Damage to property during operations</li>
                <li><strong>Vehicle Accident</strong> — Any vehicle-related incident</li>
                <li><strong>Injury</strong> — Any work-related injury (also call 911 if serious)</li>
                <li><strong>Utility Strike</strong> — Contact with underground or aerial utilities</li>
              </ul>
            </Card>
            <Tip type="danger" darkMode={darkMode}>Utility strikes must be reported immediately. Do not attempt to repair the damage — secure the area and call your supervisor.</Tip>
          </SubSection>
          </>}

          {/* ════════════════════════════════════════════════════════
              SECTION: CONTRACTOR GUIDE
             ════════════════════════════════════════════════════════ */}
          {visibleIds.has('contractor-guide') && <>
          <SectionHeader id="contractor-guide" title={sn('contractor-guide') + ' Contractor Guide'} subtitle="For contractors and crew leads — assignments, field workflows, billing, and invoicing." darkMode={darkMode} />

          <SubSection title="Dashboard Overview" darkMode={darkMode}>
            <p>Your contractor dashboard shows real-time stats pulled from the project database:</p>
            <Card darkMode={darkMode}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <div><strong>Active Segments</strong> — Segments assigned to you with total footage</div>
                <div><strong>Completed</strong> — QC-approved segments and work in progress</div>
                <div><strong>Issues</strong> — Flagged segments requiring attention</div>
              </div>
            </Card>
            <p style={{ marginTop: 12 }}>Below the stats, you'll see your <strong>top 5 assigned segments</strong> with status badges and a link to view all assignments on the project map.</p>
            <GuideImage src="/guide/09-contractor-dashboard.png" alt="Contractor Dashboard — stats cards, quick actions, and assigned segments" darkMode={darkMode} />
          </SubSection>

          <SubSection title="Accepting Assignments" darkMode={darkMode}>
            <p>When an admin assigns segments to you, they appear in your <strong>Assignments</strong> tab.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Review the assignment', desc: 'Each assignment shows the project, segment count, total footage, work type, and due date.' },
              { title: 'Accept or Decline', desc: 'Click Accept to take the work, or Decline with a reason (the admin is notified).' },
              { title: 'Assign to Sub (optional)', desc: 'After accepting, you can reassign specific segments to a sub-contractor on your team.' },
            ]} />
            <Tip type="info" darkMode={darkMode}>Accepted segments appear on your Project Map immediately.</Tip>
          </SubSection>

          <SubSection title="Project Map — Field Workflows" darkMode={darkMode}>
            <p>The Project Map is where you manage all field work. Tap any segment to open the detail panel with workflow tabs.</p>
            <WireframePlaceholder description="Segment Detail Panel — click any segment on the map to open the right-side panel with workflow tabs (Boring, Pulling, Splicing), status controls, photo uploads, and PM readings" darkMode={darkMode} />

            <h4 style={{ color: heading, marginTop: 24, marginBottom: 8 }}>Boring Workflow</h4>
            <p>Each segment progresses through these stages:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0' }}>
              <StatusBadge label="Not Started" color="#9E9E9E" />
              <span style={{ color: text }}>→</span>
              <StatusBadge label="Potholing" color="#FFB800" />
              <span style={{ color: text }}>→</span>
              <StatusBadge label="Pothole Approved" color="#2196F3" />
              <span style={{ color: text }}>→</span>
              <StatusBadge label="In Progress" color="#FFB800" />
              <span style={{ color: text }}>→</span>
              <StatusBadge label="Complete" color="#4CAF50" />
              <span style={{ color: text }}>→</span>
              <StatusBadge label="QC Approved" color="#2196F3" />
            </div>
            <StepList darkMode={darkMode} steps={[
              { title: 'Start Potholing', desc: 'Upload pothole photos showing depth and utility clearance.' },
              { title: 'Wait for Pothole Approval', desc: 'Admin reviews and approves the pothole documentation.' },
              { title: 'Verify Locates', desc: 'Confirm all utility locates are verified and current (not expired).' },
              { title: 'Start Boring', desc: 'Begin drilling. Update status to "In Progress".' },
              { title: 'Mark Complete', desc: 'Enter actual footage drilled and upload completion photos.' },
              { title: 'Await QC', desc: 'Admin reviews and approves (QC Approved) or flags for rework.' },
            ]} />

            <h4 style={{ color: heading, marginTop: 24, marginBottom: 8 }}>Pulling Workflow</h4>
            <Tip type="warning" darkMode={darkMode}>Pulling cannot start until boring is QC Approved for the segment.</Tip>
            <StepList darkMode={darkMode} steps={[
              { title: 'Select cable type', desc: 'Choose from 12F, 24F, 48F, 72F, 96F, 144F, 288F, or 432F.' },
              { title: 'Set pulling direction', desc: 'Forward or Backfeed. A warning appears if backfeed is detected based on route.' },
              { title: 'Upload 4 required photos', desc: 'Reel labels, entry point, exit point, and slack loop.' },
              { title: 'Mark Complete', desc: 'Submit for QC review.' },
            ]} />

            <h4 style={{ color: heading, marginTop: 24, marginBottom: 8 }}>Splicing Workflow</h4>
            <Tip type="warning" darkMode={darkMode}>Splicing cannot start until pulling is QC Approved.</Tip>
            <StepList darkMode={darkMode} steps={[
              { title: 'Select splice type', desc: '1x4 Splitter, 1x8 Splitter, F1/TYCO-D Closure.' },
              { title: 'Upload required photos', desc: 'Photo count varies by type (see table below).' },
              { title: 'Enter PM readings (1x4 only)', desc: 'Record power meter test results for both ports.' },
              { title: 'Mark Complete', desc: 'Submit for QC review.' },
            ]} />
          </SubSection>

          <SubSection title="Photo Requirements" darkMode={darkMode}>
            <DataTable darkMode={darkMode}
              headers={['Splice Type', 'Required Photos', 'PM Test', 'Details']}
              rows={[
                ['1x4 Splitter', '9', 'Yes (2 readings)', '7 enclosure photos + 2 PM reading photos'],
                ['1x8 Splitter', '8', 'No', '8 enclosure photos (exterior, open, installed, routing, strain relief, label, final, overview)'],
                ['F1 / TYCO-D', '5 + tray count', 'No', '5 base photos + 1 per splice tray'],
              ]}
            />
            <Tip type="info" darkMode={darkMode}>Photos are uploaded to Google Drive and linked to the segment record. GPS coordinates are extracted automatically from photo metadata when available.</Tip>
          </SubSection>

          <SubSection title="Power Meter Testing" darkMode={darkMode}>
            <p>For 1x4 splitter terminals, two power meter readings are required. The system automatically evaluates results:</p>
            <DataTable darkMode={darkMode}
              headers={['Result', 'Range', 'Meaning']}
              rows={[
                ['PASS ✅', '-25 to -8 dBm', 'Acceptable signal — within operating range'],
                ['WARNING ⚠️', '-25 to -28 dBm', 'Marginal signal — may work but near threshold'],
                ['FAIL (Weak) ❌', 'Below -28 dBm', 'Too much signal loss — check splices and connectors'],
                ['FAIL (Strong) ❌', 'Above -8 dBm', 'Signal overload risk — check for bypass or short fiber run'],
              ]}
            />
            <Tip type="danger" darkMode={darkMode}>Failing PM readings block QC approval. Re-splice and re-test until readings are within PASS range.</Tip>
          </SubSection>

          <SubSection title="Compliance Management" darkMode={darkMode}>
            <p>Your <strong>Compliance</strong> tab tracks 6 required documents. Each shows a status badge and expiration date:</p>
            <DataTable darkMode={darkMode}
              headers={['Document', 'Status Colors']}
              rows={[
                ['Certificate of Insurance (COI)', 'Green = current, Yellow = expiring soon, Red = expired'],
                ['Workers Compensation', 'Green = current, Yellow = expiring soon, Red = expired'],
                ['General Liability', 'Green = current, Yellow = expiring soon, Red = expired'],
                ['Auto Insurance', 'Green = current, Yellow = expiring soon, Red = expired'],
                ['W-9 Tax Form', 'Green = on file, Red = missing'],
                ['Master Service Agreement (MSA)', 'Green = signed, Red = not signed'],
              ]}
            />
            <p style={{ marginTop: 8 }}>Upload renewal documents directly from this tab. The system sends automatic expiration warnings at 30, 14, and 7 days before expiry.</p>
          </SubSection>

          <SubSection title="Billing & Invoices" darkMode={darkMode}>
            <p>The <strong>My Billing</strong> tab shows all your QC-approved work with contractor rates. When ready, create an invoice from completed work.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Review My Billing', desc: 'Check the billing table showing each line item with code, description, quantity, your rate, and total.' },
              { title: 'Go to Create Invoice', desc: 'Navigate to the invoice creation page from the sidebar.' },
              { title: 'Select completed work', desc: 'Check the segments/devices you want to invoice. Only QC-approved, unbilled work appears.' },
              { title: 'Add custom line items', desc: 'Optionally add extra line items (materials, extra work) not tracked in the system.' },
              { title: 'Review totals', desc: 'Verify the subtotal, 10% retainage (held back), and net payable amount.' },
              { title: 'Submit', desc: 'Submit the invoice. It appears in the admin\'s payment queue for approval.' },
            ]} />
            <GuideImage src="/guide/10-contractor-invoice.png" alt="Contractor Invoice — selected QC-approved segments with line items and 10% retainage" darkMode={darkMode} />
            <Tip type="info" darkMode={darkMode}>You only see contractor rates on your invoices. Vexus/client rates and profit margins are not visible to contractor accounts.</Tip>
          </SubSection>

          <SubSection title="Team Management" darkMode={darkMode}>
            <p>The <strong>My Team</strong> tab lists all sub-contractors registered under your company. You can view their details and see which segments are assigned to each sub.</p>
          </SubSection>
          </>}

          {/* ════════════════════════════════════════════════════════
              SECTION: SUB-CONTRACTOR GUIDE
             ════════════════════════════════════════════════════════ */}
          {visibleIds.has('sub-contractor-guide') && <>
          <SectionHeader id="sub-contractor-guide" title={sn('sub-contractor-guide') + ' Sub-Contractor Guide'} subtitle="For sub-contractors working under a primary contractor." darkMode={darkMode} />

          <SubSection title="Dashboard Overview" darkMode={darkMode}>
            <p>Your dashboard shows simplified stats focused on assigned work:</p>
            <Card darkMode={darkMode}>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li><strong>Assigned Segments</strong> — Work assigned to you by your parent contractor</li>
                <li><strong>Completed</strong> — Segments you've finished</li>
                <li><strong>Issues</strong> — Segments flagged for rework</li>
                <li><strong>Total Footage</strong> — Sum of all assigned footage</li>
              </ul>
            </Card>
          </SubSection>

          <SubSection title="Accepting Assignments" darkMode={darkMode}>
            <p>Assignments come from your parent contractor (not directly from LYT admin). You can <strong>Accept</strong> or <strong>Decline</strong> — declining notifies your parent contractor with the reason you provide.</p>
          </SubSection>

          <SubSection title="Field Work" darkMode={darkMode}>
            <p>Your field workflow is the same as a contractor: update boring/pulling/splicing status, upload photos, and enter PM readings. The only difference is that your work feeds up through your parent contractor's billing.</p>
          </SubSection>

          <SubSection title="What Sub-Contractors Cannot Access" darkMode={darkMode}>
            <Card darkMode={darkMode} style={{ background: darkMode ? '#7f1d1d20' : '#fee2e2', borderColor: '#dc2626' }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>Billing / Rates</strong> — No visibility into any billing rates or amounts</li>
                <li><strong>Invoice Creation</strong> — Subs cannot create invoices (your parent contractor handles billing)</li>
                <li><strong>Team Management</strong> — No access to manage other sub-contractors</li>
                <li><strong>Compliance Uploads</strong> — Managed by your parent contractor</li>
                <li><strong>Financial Data</strong> — No P&L, margins, or client rates</li>
              </ul>
            </Card>
          </SubSection>
          </>}

          {/* ════════════════════════════════════════════════════════
              SECTION: ADMINISTRATOR GUIDE
             ════════════════════════════════════════════════════════ */}
          {visibleIds.has('admin-guide') && <>
          <SectionHeader id="admin-guide" title={sn('admin-guide') + ' Administrator Guide'} subtitle="Complete reference for system administrators — user management, project operations, QC approval, billing, and financial oversight." darkMode={darkMode} />

          <SubSection title="Dashboard Overview" darkMode={darkMode}>
            <p>The Admin Dashboard is your command center. It shows real-time stats (total users, pending onboarding, recent submissions) and provides navigation to every admin tool.</p>
            <GuideImage src="/guide/02-admin-dashboard.png" alt="Admin Dashboard — user stats, pending onboarding, recent submissions, and sidebar navigation" darkMode={darkMode} />
          </SubSection>

          <SubSection title="User Management" darkMode={darkMode}>
            <p>Navigate to <strong>User Management</strong> to create, edit, and manage all user accounts.</p>

            <h4 style={{ color: heading, marginTop: 20, marginBottom: 8 }}>Creating a New User</h4>
            <StepList darkMode={darkMode} steps={[
              { title: 'Click "Create User"', desc: 'Opens the new user form.' },
              { title: 'Select role', desc: 'Choose Admin, Supervisor, Employee, Contractor, or Sub.' },
              { title: 'Enter details', desc: 'Name, email, phone. For contractors: company name. For subs: parent contractor.' },
              { title: 'Send invite', desc: 'A branded welcome email is sent with a link to set their password and onboarding instructions.' },
            ]} />

            <h4 style={{ color: heading, marginTop: 20, marginBottom: 8 }}>Managing Users</h4>
            <Card darkMode={darkMode}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>Search & Filter</strong> — Search by name/email/company. Filter by role and status (active/pending/inactive).</li>
                <li><strong>Edit</strong> — Update name, email, role, company, phone</li>
                <li><strong>Resend Invite</strong> — For pending users who haven't set their password</li>
                <li><strong>Reset Password</strong> — For active users who need a new password link</li>
                <li><strong>Deactivate</strong> — Disables login without deleting the account</li>
              </ul>
            </Card>
          </SubSection>

          <SubSection title="Onboarding Approval" darkMode={darkMode}>
            <p>When employees or contractors complete their onboarding forms, submissions appear in the <strong>Pending Onboarding</strong> queue on your dashboard.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Review submission', desc: 'Click to expand and view all submitted documents (W-4, direct deposit, ID, emergency contact, etc.).' },
              { title: 'Check documents', desc: 'Open Google Drive links to verify uploaded documents (ID scan, voided check, COI, etc.).' },
              { title: 'Approve or Reject', desc: 'Approve to activate the user, or reject with a reason (they\'ll be notified to resubmit).' },
            ]} />
            <Tip type="info" darkMode={darkMode}>Contractor onboarding includes a full checklist (insurance coverage, W-9, MSA, rate card acceptance) that must be reviewed before approval.</Tip>
          </SubSection>

          <SubSection title="Project Import" darkMode={darkMode}>
            <p>Import new projects from work order PDFs or pre-processed JSON data.</p>

            <h4 style={{ color: heading, marginTop: 20, marginBottom: 8 }}>PDF Import (Recommended)</h4>
            <StepList darkMode={darkMode} steps={[
              { title: 'Navigate to Import Work Orders', desc: 'From the sidebar or Admin Projects page.' },
              { title: 'Upload Work Order PDF', desc: 'The system extracts text from up to 10 pages.' },
              { title: 'Upload Map PDF', desc: 'Map pages are tiled (2x2 grid + legend crop) and analyzed by Claude Vision AI.' },
              { title: 'Review extraction', desc: 'Verify segments, splice points, structures, and line items. Check reconciliation for discrepancies.' },
              { title: 'Import', desc: 'Creates the project and populates all 8 database tables (segments, splices, handholes, etc.).' },
            ]} />
            <GuideImage src="/guide/05-job-import.png" alt="Job Import — PDF upload, extraction progress, and preview of extracted segments" darkMode={darkMode} />

            <h4 style={{ color: heading, marginTop: 20, marginBottom: 8 }}>JSON Import (Advanced)</h4>
            <p>For pre-processed data from the LYT Extractor or manual preparation, paste JSON directly. The system validates structure, checks billing codes against the rate card, and shows totals before import.</p>
          </SubSection>

          <SubSection title="QC Approval" darkMode={darkMode}>
            <p>As an admin, you are the final authority on work quality. Only admins can move segments to <strong>QC Approved</strong> status.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Open the Project Map', desc: 'Filter by status "Complete" to see segments awaiting QC.' },
              { title: 'Review the work', desc: 'Tap a segment, review photos, PM readings, footage, and notes.' },
              { title: 'Approve, Reject, or Flag', desc: 'Approve moves to QC Approved (unlocks next phase). Reject sends back for rework. Flag creates an issue.' },
            ]} />
            <Tip type="warning" darkMode={darkMode}>QC approval unlocks billing. Once a segment is QC Approved, it appears in contractor billing and can be submitted to the client.</Tip>
          </SubSection>

          <SubSection title="Bulk Assignment" darkMode={darkMode}>
            <p>Assign multiple segments to a contractor at once from the <strong>Admin Projects</strong> page.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Select work type', desc: 'Choose boring, pulling, or splicing.' },
              { title: 'Enter contractor name', desc: 'Auto-complete from registered contractors.' },
              { title: 'Select segments', desc: 'Choose by section, unassigned only, or select all.' },
              { title: 'Confirm assignment', desc: 'Review count and confirm. The contractor is notified.' },
            ]} />
          </SubSection>

          <SubSection title="Daily Production Reports → Client" darkMode={darkMode}>
            <p>Submit completed production work to the client for billing approval (Track 1 — daily production).</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Go to Daily Report', desc: 'Navigate from sidebar or Admin Projects.' },
              { title: 'Select date range and filter', desc: 'Filter by contractor, section, or specific date.' },
              { title: 'Switch to "Submit to Client" tab', desc: 'Shows QC-approved, unbilled segments with auto-generated line items.' },
              { title: 'Review line items', desc: 'Verify billing codes (UG1, UG4, UG23, etc.) and amounts against the rate card.' },
              { title: 'Enter client email and notes', desc: 'Specify the recipient and any additional context.' },
              { title: 'Submit', desc: 'Sends a branded HTML email with the production report. Segments are marked "Submitted".' },
            ]} />
            <GuideImage src="/guide/06-daily-report.png" alt="Daily Production Report — line items, totals, and client submission form" darkMode={darkMode} />

            <h4 style={{ color: heading, marginTop: 20, marginBottom: 8 }}>Tracking Submissions</h4>
            <p>In the <strong>Submission History</strong> tab, view all past submissions and log the client's response:</p>
            <Card darkMode={darkMode}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>Log Approval</strong> — When the client approves the report, mark it as Approved</li>
                <li><strong>Log Payment</strong> — When payment is received, enter amount, reference number, and date</li>
              </ul>
            </Card>
          </SubSection>

          <SubSection title="Payment Tracking" darkMode={darkMode}>
            <p>The <strong>Payment Tracker</strong> is your unified command center for all money flowing between LYT and clients.</p>
            <Card darkMode={darkMode}>
              <p><strong>Summary Cards:</strong></p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 8 }}>
                <div style={{ padding: 12, background: darkMode ? '#1e293b' : '#f1f5f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: text }}>Outstanding</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#FF9800' }}>Total unpaid</div>
                </div>
                <div style={{ padding: 12, background: darkMode ? '#1e293b' : '#f1f5f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: text }}>Awaiting Approval</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#2196F3' }}>Submitted, not approved</div>
                </div>
                <div style={{ padding: 12, background: darkMode ? '#1e293b' : '#f1f5f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: text }}>Paid This Month</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#4CAF50' }}>Payments received</div>
                </div>
              </div>
            </Card>
            <p style={{ marginTop: 8 }}>Filter by: All, Production Submissions, Invoices, Pending Approval, or Pending Payment. Click any item to expand it and see line items, submission dates, approval status, and payment details.</p>
            <GuideImage src="/guide/07-payment-tracker.png" alt="Payment Tracker — summary cards and filtered list of submissions with status badges" darkMode={darkMode} />

            <h4 style={{ color: heading, marginTop: 24, marginBottom: 8 }}>Logging Approval & Payment on Existing Submissions</h4>
            <p>When you expand a submission or invoice, action buttons appear based on its current status:</p>
            <Card darkMode={darkMode}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>Log Approval</strong> — Available on "Submitted" items. Records the client's approval date and updates segment billing status to "Approved".</li>
                <li><strong>Log Payment</strong> — Available on "Submitted" or "Approved" items. Enter the payment amount, check/ACH reference, and client invoice number. Marks segments as "Paid".</li>
              </ul>
            </Card>

            <h4 style={{ color: heading, marginTop: 24, marginBottom: 8 }}>Mark Segments Paid (Backfill Payments)</h4>
            <p>Use the <strong>"Mark Segments Paid"</strong> button in the top-right corner to record client payments for segments that were paid outside the normal submission flow — for example, when catching up on older payments.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Click "Mark Segments Paid"', desc: 'Opens a full-screen modal that loads all project segments.' },
              { title: 'Filter and search', desc: 'Use phase filters (Boring / Pulling / All), the "Hide Paid" toggle to show only unpaid segments, and the search box to find segments by ID, street name, or contractor.' },
              { title: 'Select segments', desc: 'Check individual segments or use "Select all visible" to check everything matching your filters. Each line shows the segment ID, street, phase, footage, work status, billing status, and calculated amount.' },
              { title: 'Enter payment details', desc: 'Fill in the payment date, check/ACH reference number, client invoice number, and optional notes.' },
              { title: 'Confirm', desc: 'Click "Mark X Segments Paid — $Y" to record the payment. A submission record is created with status "Paid" and each selected segment\'s billing status is updated.' },
            ]} />
            <Tip type="info" darkMode={darkMode}>Amounts are calculated automatically using Vexus billing rates (e.g., UG1 for single-duct boring, UG4 for pulling) multiplied by footage. The running total updates as you select segments.</Tip>
            <Tip type="warning" darkMode={darkMode}>Once segments are marked as Paid, you can safely pay the contractors who performed that work — the client revenue side is confirmed.</Tip>
          </SubSection>

          <SubSection title="Client Invoices (Track 2)" darkMode={darkMode}>
            <p>For splicing, special builds, and maintenance work — create traditional invoices separate from daily production reports.</p>
            <StepList darkMode={darkMode} steps={[
              { title: 'Select invoice type', desc: 'Splicing, Special Build, or Maintenance.' },
              { title: 'Select devices', desc: 'Choose from QC-approved, unbilled splice points and structures.' },
              { title: 'Review auto-generated line items', desc: 'Ring cuts (FS2), case setup (FS4), fusion splices (FS1), PM tests (FS3), structures at unit rates.' },
              { title: 'Add custom items if needed', desc: 'For work not automatically tracked in the system.' },
              { title: 'Verify totals', desc: 'Subtotal, 10% retainage (held back), and net amount.' },
              { title: 'Submit to client', desc: 'Sends branded email and tracks the invoice through approval → payment.' },
            ]} />
          </SubSection>

          <SubSection title="P&L Dashboard" darkMode={darkMode}>
            <p>View profit and loss at a glance — revenue (Vexus rates), cost (contractor payments), and gross margin.</p>
            <Card darkMode={darkMode}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>Revenue</strong> — What Vexus pays LYT, calculated from QC-approved work at Vexus rates</li>
                <li><strong>Cost</strong> — What LYT pays contractors, from submitted invoices</li>
                <li><strong>Gross Margin</strong> — Revenue minus cost, with percentage</li>
                <li><strong>Retainage</strong> — 10% held from contractor invoices (both directions)</li>
              </ul>
            </Card>
            <Tip type="danger" darkMode={darkMode}>P&L data is admin-only. Contractors never see Vexus rates, revenue figures, or margin calculations.</Tip>
          </SubSection>

          <SubSection title="Client Invoicing" darkMode={darkMode}>
            <p>Bill Vexus for completed work using the <strong>Client Invoice</strong> page. This is the revenue side — what Vexus owes LYT. Select QC-approved segments, generate line items at Vexus rates, and track submission through payment.</p>
          </SubSection>

          <SubSection title="Additional Admin Tools" darkMode={darkMode}>
            <Card darkMode={darkMode}>
              <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2.2 }}>
                <li><strong>Metrics Dashboard</strong> — KPIs, production trends, contractor performance, quality metrics</li>
                <li><strong>Activity Log</strong> — Full audit trail of all system actions (logins, status updates, QC approvals, billing)</li>
                <li><strong>Recruiting Pipeline</strong> — Candidate tracking from Lead → Contacted → Interview → Offer → Hired</li>
                <li><strong>Work Map (GPS)</strong> — Real-time crew locations, auto-refresh every 30 seconds</li>
                <li><strong>Segment Reassignment</strong> — Bulk reassign work between contractors</li>
                <li><strong>Asset Management</strong> — Track company equipment, vehicles, and tools</li>
                <li><strong>Change Orders</strong> — Create, approve, and track change orders per project</li>
              </ul>
            </Card>
          </SubSection>
          </>}

          {/* ════════════════════════════════════════════════════════
              SECTION: QUICK REFERENCE
             ════════════════════════════════════════════════════════ */}
          {visibleIds.has('quick-reference') && <>
          <SectionHeader id="quick-reference" title={sn('quick-reference') + ' Quick Reference'} subtitle="At-a-glance tables for status colors, photo requirements, billing codes, and more." darkMode={darkMode} />

          <SubSection title="Status Colors" darkMode={darkMode}>
            <DataTable darkMode={darkMode}
              headers={['Status', 'Color', 'Meaning']}
              rows={[
                [<StatusBadge label="Not Started" color="#FF4444" />, 'Red', 'Work has not begun'],
                [<StatusBadge label="In Progress" color="#FFB800" />, 'Yellow', 'Crew is actively working this segment'],
                [<StatusBadge label="Complete" color="#4CAF50" />, 'Green', 'Work finished, awaiting QC review'],
                [<StatusBadge label="QC Approved" color="#2196F3" />, 'Blue', 'Admin approved — billable'],
                [<StatusBadge label="Issue" color="#FF9800" />, 'Orange', 'Flagged problem — needs attention'],
                [<StatusBadge label="Blocked" color="#9E9E9E" />, 'Grey', 'Cannot proceed (prerequisite not met)'],
              ]}
            />
          </SubSection>

          {(viewRole === 'admin' || viewRole === 'contractor') &&
          <SubSection title="Billing Status Colors" darkMode={darkMode}>
            <DataTable darkMode={darkMode}
              headers={['Billing Status', 'Color', 'Meaning']}
              rows={[
                [<StatusBadge label="Not Billed" color="#9E9E9E" />, 'Grey', 'Work not yet submitted for billing'],
                [<StatusBadge label="Submitted" color="#FFB800" />, 'Yellow', 'Sent to client, awaiting approval'],
                [<StatusBadge label="Approved" color="#2196F3" />, 'Blue', 'Client approved, payment expected'],
                [<StatusBadge label="Paid" color="#4CAF50" />, 'Green', 'Payment received'],
              ]}
            />
          </SubSection>}

          <SubSection title="Photo Requirements by Splice Type" darkMode={darkMode}>
            <DataTable darkMode={darkMode}
              headers={['Type', 'Photos', 'PM Test?', 'Photo List']}
              rows={[
                ['1x4 Splitter', '9', 'Yes', 'Exterior, Open, Installed, Routing, Strain Relief, Label, Final Closed, PM Test 1, PM Test 2'],
                ['1x8', '8', 'No', 'Exterior, Open, Installed, Routing, Strain Relief, Label, Final Closed, Overview'],
                ['F1 / TYCO-D', '5 + trays', 'No', 'Exterior, Open, Cable Entry, Final Closed, Label, + 1 per tray'],
              ]}
            />
          </SubSection>

          <SubSection title="Power Meter Thresholds" darkMode={darkMode}>
            <DataTable darkMode={darkMode}
              headers={['Range (dBm)', 'Result', 'Action']}
              rows={[
                ['-8 to -25', 'PASS', 'Proceed — within operating range'],
                ['-25 to -28', 'WARNING', 'Marginal — re-check, may need re-splice'],
                ['Below -28', 'FAIL (Weak)', 'Too much loss — re-splice and re-test'],
                ['Above -8', 'FAIL (Strong)', 'Overload risk — check fiber path'],
              ]}
            />
          </SubSection>

          <SubSection title="Common Billing Codes" darkMode={darkMode}>
            <DataTable darkMode={darkMode}
              headers={['Code', 'Description', 'Unit']}
              rows={[
                ['UG1', 'Bore 1x1.25" subduct', 'LF'],
                ['UG2', 'Bore 2x1.25" subduct', 'LF'],
                ['UG3', 'Bore 3x1.25" subduct', 'LF'],
                ['UG4', 'Pull fiber cable (up to 144ct)', 'LF'],
                ['UG9', 'Install pedestal', 'EA'],
                ['UG10', 'Install handhole 15x20x12', 'EA'],
                ['UG11', 'Install handhole 17x30x18', 'EA'],
                ['UG12', 'Install flowerpot / utility box', 'EA'],
                ['UG14', 'Install ground rod', 'EA'],
                ['UG23', 'Bore 1x2" HDPE', 'LF'],
                ['UG24', 'Bore 4" HDPE', 'LF'],
                ['UG28', 'Pull armored cable (288-432ct)', 'LF'],
                ['FS1', 'Fusion splice', 'EA'],
                ['FS2', 'Ring cut / mid-span access', 'EA'],
                ['FS3', 'Test fiber (PM reading)', 'EA'],
                ['FS4', 'Re-enter / case setup', 'EA'],
              ]}
            />
          </SubSection>

          {viewRole === 'admin' && <SubSection title="Onboarding Invite Codes" darkMode={darkMode}>
            <DataTable darkMode={darkMode}
              headers={['Purpose', 'Code']}
              rows={[
                ['Employee / Contractor Onboarding', 'welcome2lyt'],
                ['NDA Signing', 'lytnda2026'],
              ]}
            />
          </SubSection>}

          <SubSection title="Contact & Support" darkMode={darkMode}>
            <Card darkMode={darkMode}>
              <p style={{ fontSize: 16, fontWeight: 600, color: heading, marginBottom: 12 }}>LYT Communications</p>
              <p><strong>Address:</strong> {LYT_INFO.address}, {LYT_INFO.city}, {LYT_INFO.state} {LYT_INFO.zip}</p>
              <p><strong>Phone:</strong> {LYT_INFO.phone}</p>
              <p><strong>Email:</strong> {LYT_INFO.email}</p>
              <p style={{ marginTop: 12 }}><strong>Admin Contacts:</strong></p>
              <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                <li>matt@lytcomm.com</li>
                <li>mason@lytcomm.com</li>
                <li>donnie@lytcomm.com</li>
              </ul>
              <p style={{ marginTop: 12 }}><strong>Emergency (24/7):</strong> {LYT_INFO.phone}</p>
            </Card>
          </SubSection>
          </>}

          {/* Footer */}
          <div style={{ borderTop: `1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}`, marginTop: 48, paddingTop: 24, textAlign: 'center', color: text, fontSize: 13 }}>
            <p>© {new Date().getFullYear()} {LYT_INFO.name} — All rights reserved</p>
            <p style={{ marginTop: 4 }}>Platform User Guide v1.0 — March 2026</p>
          </div>

        </div>
      </div>
    </div>
  );
}
